import styled, { keyframes } from "styled-components";
import { DefaultButton, PrimaryButton } from "@fluentui/react";

// Animaciones
const fadeIn = keyframes`
    from
    {
        opacity: 0;
        transform: translateY(-10px);
    }
    to
    {
        opacity: 1;
        transform: translateY(0);
    }
`;

const pulse = keyframes`
    0%
    {
        transform: scale(1);
    }
    50%
    {
        transform: scale(1.05);
    }
    100%
    {
        transform: scale(1);
    }
`;

const spin = keyframes`
    0%
    {
        transform: rotate(0deg);
    }
    100%
    {
        transform: rotate(360deg);
    }
`;

// Contenedor principal del modal
export const ModalContainer = styled.div`
    padding: 2em 1em 1em 2em;
    background: white;
    border-radius: 8px;
    max-height: 80vh;
    overflow-y: auto;
`;

// Título del modal
export const ModalTitle = styled.h2`
    margin: 0 0 0.5em 0 !important;
    font-size: 24px;
    font-weight: 600;
    color: #323130;
    cursor: default;
`;

// Contenedor del contenido
export const ContentContainer = styled.div`
    margin: 16px 0 24px 0;
    padding: 0 16px 0 0;
`;

// Fila de información
export const InfoRow = styled.div`
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border-left: 4px solid #0078d4;
`;

export const InfoText = styled.p`
    margin: 0 !important;
    font-size: 14px;
    color: #323130;
    line-height: 1.5;
    cursor: default;
`;

// Contenedor para la fecha
export const DateContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    padding: 12px 16px;
    background-color: #f8f9fa;
    border: 1px solid #e1dfdd;
    border-radius: 4px;
`;

export const DateLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #323130;
    margin: 0;
    white-space: nowrap;
`;

// Contenedor para el nombre de la composición
export const CompositionNameContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    margin-left: auto;
`;

export const CompositionNameLabel = styled.label`
    font-size: 14px;
    font-weight: 600;
    color: #323130;
    margin: 0;
    white-space: nowrap;
`;

// Contenedor del buscador
export const SearchContainer = styled.div`
    margin-bottom: 24px;
    position: relative;
`;

export const SearchLabel = styled.label`
    display: block;
    font-size: 14px;
    font-weight: 600;
    color: #323130;
    margin-bottom: 8px;
    cursor: default;
`;

export const SearchInputContainer = styled.div`
    position: relative;
    width: 100%;
`;

export const SearchIconContainer = styled.div`
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #605e5c;
    pointer-events: none;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1;
`;

export const ClearButton = styled.button`
    position: absolute;
    right: 36px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    color: #605e5c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: all 0.2s ease;
    z-index: 2;
    line-height: 1;
    
    &:hover
    {
        background-color: #f3f2f1;
        color: #323130;
    }
    
    &:active
    {
        background-color: #edebe9;
    }
`;

export const LoadingSpinner = styled.div`
    width: 16px;
    height: 16px;
    border: 2px solid #f3f2f1;
    border-top: 2px solid #0078d4;
    border-radius: 50%;
    animation: ${spin} 1s linear infinite;
`;

export const SearchingContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 24px 16px;
    gap: 12px;
    cursor: default;
`;

export const SearchingText = styled.div`
    font-size: 14px;
    color: #605e5c;
    font-weight: 500;
    cursor: default;
`;

// Contenedor de resultados de búsqueda
export const SearchResultsContainer = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #e1dfdd;
    border-top: none;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 200px;
    overflow-y: auto;
    animation: ${fadeIn} 0.2s ease-out;
`;

export const SearchResultItem = styled.div<{$isAvailable: boolean}>`
    padding: 12px 16px;
    cursor: ${props => props.$isAvailable ? 'pointer' : 'not-allowed'};
    border-bottom: 1px solid #f3f2f1;
    transition: background-color 0.2s ease;
    background-color: ${props => props.$isAvailable ? 'white' : '#a4a8ab'};
    color: ${props => props.$isAvailable ? '#323130' : 'white !important'};
    opacity: ${props => props.$isAvailable ? '1' : '0.6'};
    
    &:hover
    {
        background-color: ${props => props.$isAvailable ? '#dce3e9' : '#5f6367'};
        color: ${props => props.$isAvailable ? '#323130' : 'white !important'};
    }
    
    &:last-child
    {
        border-bottom: none;
    }
`;

export const SearchResultName = styled.div`
    font-size: 14px;
    font-weight: 600;
    color: inherit;
    margin-bottom: 4px;
`;

export const SearchResultDetails = styled.div`
    font-size: 12px;
    color: inherit;
    display: flex;
    gap: 16px;
    
    span
    {
        white-space: nowrap;
    }
`;

export const SpanNotAvailable = styled.span`
    color: #d13438;
    font-weight: bold;

    ${SearchResultItem}:hover &
    {
        color: #cd7f81;
        font-weight: bold;
    }
`;

export const NoResultsMessage = styled.div`
    padding: 16px;
    text-align: center;
    color: #605e5c;
    font-size: 14px;
    background-color: #f8f9fa;
    border: 1px solid #e1dfdd;
    border-top: none;
    border-radius: 0 0 8px 8px;
    cursor: default;
`;

// Contenedor de fondos seleccionados
export const SelectedFundsContainer = styled.div`
    margin-top: 24px;
    cursor: default;
`;

export const SelectedFundsTitle = styled.h3`
    margin: 0 0 16px 0 !important;
    font-size: 16px;
    font-weight: 600;
    color: #323130;
    cursor: default;
`;

// Tabla de fondos
export const FundsTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    border: 1px solid #e1dfdd;
    border-radius: 8px;
    overflow: hidden;
    background-color: white;
`;

export const TableHeader = styled.thead`
    background-color: #f3f2f1;
`;

export const TableHeaderCell = styled.th`
    padding: 12px 16px;
    text-align: left;
    font-weight: 600;
    color: #323130;
    border-bottom: 1px solid #e1dfdd;
    white-space: nowrap;
    
    &:first-child
    {
        width: 40%;
    }
    &:nth-child(2)
    {
        width: 10%;
    }
    &:nth-child(3)
    {
        width: 25%;
    }
    &:nth-child(4)
    {
        width: 10%;
        text-align: right;
    }
    &:last-child
    {
        width: 15%;
        text-align: center;
    }
    
    .edit-mode &
    {
        &:first-child
        {
            width: 50%;
        }
        &:nth-child(2)
        {
            width: 15%;
        }
        &:nth-child(3)
        {
            width: 15%;
            text-align: right;
        }
        &:last-child
        {
            width: 20%;
            text-align: center;
        }
    }
`;

export const TableBody = styled.tbody``;

export const TableRow = styled.tr`
    border-bottom: 1px solid #f3f2f1;
    
    &:hover
    {
        background-color: #f8f9fa;
    }
    
    &:last-child
    {
        border-bottom: none;
    }
`;

export const TableCell = styled.td`
    padding: 12px 16px;
    color: #323130;
    vertical-align: middle;
    
    &:first-child
    {
        font-weight: 500;
    }
`;

// Input de porcentaje
export const PercentageInput = styled.input`
    width: 100%;
    padding: 6px 8px;
    border: 1px solid #d2d0ce;
    border-radius: 4px;
    font-size: 14px;
    text-align: right;
    
    &:focus
    {
        outline: none;
        border-color: #0078d4;
        box-shadow: 0 0 0 1px #0078d4;
    }
    
    &:invalid
    {
        border-color: #d13438;
    }
`;

// Contenedor para los botones de acción
export const ActionButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 8px;
`;

// Botón de max
export const MaxButton = styled.button`
    padding: 4px 8px;
    background-color: #0078d4;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex: 1;
    
    &:hover
    {
        background-color: #106ebe;
    }
`;

// Botón de eliminar
export const RemoveButton = styled.button`
    padding: 4px 8px;
    background-color: #CC214F;
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 12px;
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex: 1;
    
    &:hover
    {
        background-color: #a4262c;
    }
    
    &:active
    {
        background-color: #8a1a1a;
    }
`;

// Resumen de porcentajes
export const PercentageSummary = styled.div`
    margin-top: 16px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #e1dfdd;
    text-align: center;
`;

export const PercentageTotal = styled.div<{$isValid: boolean}>`
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.$isValid ? '#107c10' : '#d13438'};
    cursor: default;
`;

export const PercentageWarning = styled.div`
    font-size: 14px;
    color: #d13438;
    font-weight: 500;
`;

// Estado vacío
export const EmptyStateContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 48px 24px;
    text-align: center;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 2px dashed #e1dfdd;
`;

export const EmptyStateIcon = styled.div`
    width: 48px;
    height: 48px;
    background-color: #e1dfdd;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    color: #605e5c;
    font-size: 24px;
`;

export const EmptyStateText = styled.p`
    margin: 0;
    font-size: 14px;
    color: #605e5c;
    max-width: 500px;
    line-height: 1.5;
`;

// Mensaje de validación
export const ValidationMessage = styled.div`
    padding: 12px 16px;
    margin: 0 !important;
    margin-right: 16px !important;
    background-color: #fef7f7;
    border: 1px solid #d13438;
    border-radius: 4px;
    color: #d13438;
    font-size: 14px;
    font-weight: 500;
    text-align: center;
`;

// Contenedor de botones
export const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    padding: 0 16px;
    margin-top: 32px;
    border-top: 1px solid #e1dfdd;
    padding-top: 24px;
`;

export const CancelButton = styled(DefaultButton)`
    background-color: #CC214F !important;
    color: white !important;
    border-color: #CC214F !important;
    min-width: 100px;
    height: 40px;
    
    &:hover
    {
        background-color: #a4262c !important;
        border-color: #a4262c !important;
    }
    
    &:active
    {
        background-color: #8a1a1a !important;
        border-color: #8a1a1a !important;
    }
`;

export const SaveButton = styled(PrimaryButton)`
    min-width: 140px;
    height: 40px;
    
    &:disabled
    {
        background-color: #f3f2f1 !important;
        color: #a19f9d !important;
        border-color: #e1dfdd !important;
        cursor: not-allowed;
    }
    
    &:not(:disabled):hover
    {
        animation: ${pulse} 0.3s ease-in-out;
    }
`;

// Responsive design
export const ResponsiveContainer = styled.div`
    @media(max-width: 768px)
    {
        ${ModalContainer}
        {
            padding: 1em 0.5em 0.5em 1em;
        }
        
        ${FundsTable}
        {
            font-size: 12px;
        }
        
        ${TableHeaderCell}, ${TableCell}
        {
            padding: 8px 12px;
        }
        
        ${SearchResultItem}
        {
            padding: 8px 12px;
        }
        
        ${SearchResultName}
        {
            font-size: 13px;
        }
        
        ${SearchResultDetails}
        {
            font-size: 11px;
            flex-direction: column;
            gap: 4px;
        }
        
        ${ButtonContainer}
        {
            flex-direction: column;
            gap: 12px;
        }
        
        ${CancelButton}, ${SaveButton}
        {
            width: 100%;
        }
    }
    
    @media(max-width: 480px)
    {
        ${ModalContainer}
        {
            padding: 0.5em;
        }
        
        ${ContentContainer}
        {
            padding: 0;
        }
        
        ${FundsTable}
        {
            font-size: 11px;
        }
        
        ${TableHeaderCell}, ${TableCell}
        {
            padding: 6px 8px;
        }
        
        ${PercentageInput}
        {
            font-size: 12px;
            padding: 4px 6px;
        }
        
        ${RemoveButton}
        {
            font-size: 11px;
            padding: 3px 6px;
        }
    }
`;

export const PortfolioModelModificationContainer = styled.div`
    margin: 16px 0;
    padding: 1em;
    background-color: #FFC107;
    border: 1px solid #e1e5e9;
    border-radius: 8px;
    cursor: default;
`;

export const ModificationToggleContainer = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    
    .ms-Toggle
    {
        display: flex;
        align-items: center;
        
        .ms-Toggle-innerContainer
        {
            display: flex;
            align-items: center;
        }
        
        .ms-Toggle-label
        {
            display: flex;
            align-items: center;
            margin-left: 12px;
        }
    }
`;

export const ModificationLabel = styled.label`
    font-size: 15px !important;
    font-weight: 600 !important;
    color: #323130;
    margin: 0;
`;

export const ModificationInfoText = styled.p`
    font-size: 12px;
    color:#323130;
    margin: 0;
    line-height: 1.4;
    cursor: default;
    padding-top: 0.5em;
`;